import React from 'react'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { Grid } from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { es } from 'date-fns/locale/es'
import { format, parse } from 'date-fns'

/**
 * @typedef {object} FilterDateChangeEvent
 * @property {object} target - Un objeto que simula la estructura de un evento de cambio nativo.
 * @property {string} target.name - El nombre del campo, proveniente de la prop name.
 * @property {string} target.value - La fecha en formato 'dd/MM/yyyy' o string vacío si se limpia.
 */

/**
 * @typedef {object} FilterDateProps
 * @property {string} [label] - Etiqueta opcional que se muestra antes del selector de fecha.
 * @property {string} [value] - Valor actual de la fecha en formato 'dd/MM/yyyy'.
 * @property {string} name - Nombre del campo para identificación en el evento onChange.
 * @property {function(FilterDateChangeEvent): void} onChange - Callback ejecutado cuando cambia la fecha.
 * @property {string} [defaultValue] - Valor por defecto en formato 'dd/MM/yyyy' si value no está presente.
 * @property {string} [minDate] - Fecha mínima seleccionable en formato 'dd/MM/yyyy'.
 * @property {string} [maxDate] - Fecha máxima seleccionable en formato 'dd/MM/yyyy'.
 */

/**
 * Un componente selector de fechas optimizado para filtros.
 *
 * Proporciona un DatePicker configurado para filtros con formato de fecha español (dd/MM/yyyy),
 * localización en español, y restricción por defecto para deshabilitar fechas futuras.
 * Las fechas se manejan como strings en formato dd/MM/yyyy tanto en entrada como en salida.
 *
 * @component
 * @example
 * const MyFilters = () => {
 *   const [fechaDesde, setFechaDesde] = useState('');
 *   const [fechaHasta, setFechaHasta] = useState('');
 *
 *   const handleChangeDesde = (e) => {
 *     setFechaDesde(e.target.value);
 *     // Aplicar filtro...
 *   };
 *
 *   const handleChangeHasta = (e) => {
 *     setFechaHasta(e.target.value);
 *     // Aplicar filtro...
 *   };
 *
 *   return (
 *     <>
 *       <FilterDate
 *         label="Desde"
 *         name="fechaDesde"
 *         value={fechaDesde}
 *         onChange={handleChangeDesde}
 *         maxDate={fechaHasta}
 *       />
 *       <FilterDate
 *         label="Hasta"
 *         name="fechaHasta"
 *         value={fechaHasta}
 *         onChange={handleChangeHasta}
 *         minDate={fechaDesde}
 *       />
 *     </>
 *   );
 * };
 *
 * @param {FilterDateProps} props - Las props del componente FilterDate.
 */
export const FilterDate = ({
  label,
  value,
  name,
  onChange,
  defaultValue,
  minDate,
  maxDate,
  ...props
}) => {
  const parseDate = (dateString) => {
    if (!dateString) return null
    const parsed = parse(dateString, 'dd/MM/yyyy', new Date())
    return isNaN(parsed) ? null : parsed
  }

  const objectDate = parseDate(value) || parseDate(defaultValue)
  const minDateValue = parseDate(minDate)
  const maxDateValue = parseDate(maxDate)

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Grid container justifyContent="space-around" alignItems="center" sx={{ padding: '8px 0' }}>
        {label && <div sx={{ marginRight: 2, fontWeight: 'bold' }}>{label}</div>}
        <DatePicker
          format="dd/MM/yyyy"
          name={name}
          value={objectDate}
          onChange={(date) => {
            if (date && date.toString() !== 'Invalid Date') {
              const formattedDate = format(date, 'dd/MM/yyyy')
              onChange({ target: { name, value: formattedDate } })
            } else {
              onChange({ target: { name, value: '' } })
            }
          }}
          disableFuture
          minDate={minDateValue}
          maxDate={maxDateValue}
          slotProps={{
            textField: {
              size: 'small',
              placeholder: 'dd/mm/aaaa',
            },
          }}
          sx={{ width: '180px' }}
          {...props}
        />
      </Grid>
    </LocalizationProvider>
  )
}

export default FilterDate
